import {
  Alert,
  ButtonIcon,
  Column,
  Columns,
  Heading,
  IconChevron,
  Text,
} from 'braid-design-system';
import { useId, type ComponentProps } from 'react';
import { SmartTextLink } from 'scoobie';
import { BlockLoadingPlaceholder } from '#src/components/LoadingPlaceholder/LoadingPlaceholder.tsx';

import type * as Statuspage from '#src/types/statuspage.ts';

/**
 * Maps a rolled up status indicator to an `Alert` tone
 *
 * <https://support.atlassian.com/statuspage/docs/top-level-status-and-incident-impact-calculations/>
 */
const TOP_LEVEL_STATUS_INDICATOR_TONE: Record<
  Statuspage.TopLevelStatusIndicator,
  ComponentProps<typeof Alert>['tone']
> = {
  none: 'positive',
  minor: 'caution',
  major: 'critical',

  // It's unclear in what situation qualifies as critical. Possibly global
  // thermonuclear war?
  critical: 'critical',
};

/**
 * Renders Atlassian Statuspage's rolled up status of the entire SEEK API
 */
export const TopLevelStatus = ({
  status,
  pageName,
  statusPageUrl,
  expanded,
  setExpanded,
}: {
  status: Statuspage.TopLevelStatus;
  pageName: string;
  statusPageUrl: string;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}) => (
  <Alert tone={TOP_LEVEL_STATUS_INDICATOR_TONE[status.indicator]}>
    <Columns space="gutter" collapseBelow="tablet">
      <Column>
        <Heading level="4">{pageName} status</Heading>
      </Column>

      <Column width="content">
        <Text>
          <SmartTextLink href={statusPageUrl}>
            {status.description}
          </SmartTextLink>
        </Text>
      </Column>

      <Column width="content" hideBelow="tablet">
        <ButtonIcon
          icon={<IconChevron direction={expanded ? 'up' : 'down'} />}
          onClick={() => setExpanded((e) => !e)}
          id={useId()}
          variant="transparent"
          label={expanded ? 'Hide details' : 'Show details'}
          bleed
        />
      </Column>
    </Columns>
  </Alert>
);

export const TopLevelStatusPlaceholder = () => (
  <BlockLoadingPlaceholder>
    <Alert tone="caution">
      <Heading level="4">SEEK API status</Heading>
    </Alert>
  </BlockLoadingPlaceholder>
);
